import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';

export interface CardProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'outlined' | 'elevated' | 'knowledge' | 'creativity';
  interactive?: boolean;
  className?: string;
  onClick?: () => void;
  animated?: boolean;
  icon?: ReactNode;
  footer?: ReactNode;
  fullWidth?: boolean;
}

const cardVariants = {
  hover: { 
    y: -5,
    boxShadow: '0 12px 20px -3px rgba(0, 0, 0, 0.1), 0 8px 8px -2px rgba(0, 0, 0, 0.05)',
    transition: { 
      duration: 0.2,
      ease: 'easeInOut'
    }
  },
  tap: { 
    y: 0,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    scale: 0.98,
    transition: { 
      duration: 0.1
    }
  },
  initial: { 
    y: 0,
    scale: 1,
  }
};

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  variant = 'default',
  interactive = false,
  className = '',
  onClick,
  animated = true,
  icon,
  footer,
  fullWidth = false,
}) => {
  // Define base and variant-specific classes
  const baseClasses = 'rounded-xl transition-all duration-300';
  
  const variantClasses = {
    default: 'bg-white shadow-edu-card',
    outlined: 'bg-white border border-gray-200',
    elevated: 'bg-white shadow-lg',
    knowledge: 'bg-knowledge-light/10 border-l-4 border-knowledge-DEFAULT',
    creativity: 'bg-creativity-light/10 border-l-4 border-creativity-DEFAULT',
  };

  const interactiveClasses = interactive 
    ? 'cursor-pointer hover:shadow-lg' 
    : '';
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const cardClasses = `${baseClasses} ${variantClasses[variant]} ${interactiveClasses} ${widthClass} ${className}`.trim();

  const content = (
    <>
      <div className="p-5">
        {(title || icon) && (
          <div className="flex items-start mb-4">
            {icon && <div className="mr-3 mt-0.5">{icon}</div>}
            <div>
              {title && <h3 className="text-xl font-semibold font-display text-gray-800">{title}</h3>}
              {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>
          </div>
        )}
        <div className={`${title || icon ? 'mt-4' : ''}`}>{children}</div>
      </div>
      
      {footer && (
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-100">
          {footer}
        </div>
      )}
    </>
  );

  if (interactive && animated) {
    return (
      <motion.div
        className={cardClasses}
        onClick={onClick}
        variants={cardVariants}
        initial="initial"
        whileHover="hover"
        whileTap="tap"
      >
        {content}
      </motion.div>
    );
  }

  return (
    <div className={cardClasses} onClick={onClick}>
      {content}
    </div>
  );
}; 